import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Pagination,
  Divider,
  useTheme,
  Paper,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  ListItemIcon,
  ListItemText,
  alpha,
  SelectChangeEvent,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FilterListIcon from '@mui/icons-material/FilterList';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import SortIcon from '@mui/icons-material/Sort';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

import { toast } from 'react-toastify';
import { uploadService, MediaItem, StorageUsage } from '../../../services/api/uploadService';
import { useAuth } from '../../../contexts/AuthContext';
import MediaUpload from '../../../components/Admin/MediaUpload';

const AdminMedia: React.FC = () => {
  const theme = useTheme();
  const { tokens } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<MediaItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [mediaType, setMediaType] = useState<'all' | 'image' | 'video'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // States for the upload dialog
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [uploadedAltText, setUploadedAltText] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadTags, setUploadTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  
  // State for file detail modal
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [updatedAltText, setUpdatedAltText] = useState('');
  const [updatedCategory, setUpdatedCategory] = useState('');
  const [updatedTags, setUpdatedTags] = useState<string[]>([]);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  
  // State for context menu
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    mediaId: string;
  } | null>(null);

  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Add state for storage usage
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [loadingStorage, setLoadingStorage] = useState(false);

  // Load media items and storage usage on component mount
  useEffect(() => {
    const loadMediaItems = async () => {
      if (!tokens?.idToken) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const items = await uploadService.getAllMedia(tokens.idToken);
        setMediaItems(items);
        setFilteredItems(items);
        
        // Also fetch storage usage
        const usage = await uploadService.getStorageUsage(tokens.idToken);
        setStorageUsage(usage);
      } catch (error) {
        console.error('Error loading media items:', error);
        toast.error('Failed to load media items');
      } finally {
        setLoading(false);
      }
    };
    
    loadMediaItems();
  }, [tokens?.idToken]);

  // Filter media items whenever filters change
  useEffect(() => {
    let filtered = [...mediaItems];
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.altText?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by media type
    if (mediaType !== 'all') {
      filtered = filtered.filter(item => item.type === mediaType);
    }
    
    // Sort items
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return sortDirection === 'asc' 
          ? a.uploadDate.getTime() - b.uploadDate.getTime()
          : b.uploadDate.getTime() - a.uploadDate.getTime();
      } else if (sortBy === 'name') {
        return sortDirection === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else { // size
        return sortDirection === 'asc'
          ? a.size - b.size
          : b.size - a.size;
      }
    });
    
    setFilteredItems(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [mediaItems, searchTerm, mediaType, sortBy, sortDirection]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const pageCount = Math.ceil(filteredItems.length / itemsPerPage);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle media type filter change
  const handleMediaTypeChange = (event: React.MouseEvent<HTMLElement>, newType: 'all' | 'image' | 'video' | null) => {
    if (newType !== null) {
      setMediaType(newType);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'grid' | 'list' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Handle sort change
  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as 'date' | 'name' | 'size');
  };

  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Handle pagination
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  // Open the upload dialog
  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true);
    setUploadedFileUrl('');
    setUploadedAltText('');
    setUploadCategory('');
    setUploadTags([]);
  };

  // Close the upload dialog
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  // Handle successful upload
  const handleUploadSuccess = (url: string, altText?: string) => {
    setUploadedFileUrl(url);
    setUploadedAltText(altText || '');
  };

  // Add uploaded file to the media library
  const handleSaveUpload = async () => {
    if (!uploadedFileUrl || !tokens?.idToken) {
      toast.warn('Please upload a file first');
      return;
    }

    try {
      setUploadingFile(true);
      
      // Determine if it's an image or video based on the URL
      const isVideo = uploadedFileUrl.includes('.mp4') || 
                      uploadedFileUrl.includes('.webm') || 
                      uploadedFileUrl.includes('.avi') ||
                      uploadedFileUrl.includes('youtube.com') || 
                      uploadedFileUrl.includes('youtu.be') ||
                      uploadedFileUrl.includes('vimeo.com');

      // Extract filename from URL
      const fileName = uploadedFileUrl.split('/').pop() || 'unnamed-file';
      
      // Create new media item
      const newItem: MediaItem = {
        id: Date.now().toString(), // This will be replaced by the backend
        url: uploadedFileUrl,
        type: isVideo ? 'video' : 'image',
        name: fileName,
        size: 0, // We don't have the actual size here
        uploadDate: new Date(),
        category: uploadCategory,
        tags: uploadTags,
        altText: uploadedAltText,
      };
      
      // Add to media items (in a real app, this would be handled by the backend)
      setMediaItems(prev => [newItem, ...prev]);
      
      // Close dialog and show success message
      setOpenUploadDialog(false);
      
      // Refresh the media items list to show the new upload
      try {
        const updatedItems = await uploadService.getAllMedia(tokens.idToken);
        setMediaItems(updatedItems);
        
        // Refresh storage usage
        const usage = await uploadService.getStorageUsage(tokens.idToken);
        setStorageUsage(usage);
      } catch (refreshError) {
        console.error('Failed to refresh media list:', refreshError);
        // Even if refresh fails, we already added the item to local state
      }
      
      toast.success('Media successfully added to library');
      
      // Reset upload form state
      setUploadedFileUrl('');
      setUploadedAltText('');
      setUploadCategory('');
      setUploadTags([]);
    } catch (error) {
      console.error('Failed to save media:', error);
      toast.error('Failed to save media to library');
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle tag input
  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  // Add a tag when Enter is pressed
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!uploadTags.includes(tagInput.trim())) {
        setUploadTags([...uploadTags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  // Remove a tag
  const handleDeleteTag = (tagToDelete: string) => {
    setUploadTags(uploadTags.filter(tag => tag !== tagToDelete));
  };

  // Open file details
  const handleOpenDetails = async (item: MediaItem) => {
    setSelectedMedia(item);
    // Initialize editing fields with current values
    setUpdatedAltText(item.altText || '');
    setUpdatedCategory(item.category || '');
    setUpdatedTags(item.tags || []);
    setEditingMetadata(false);
    setOpenDetailDialog(true);
    
    // If it's a video, refresh the URL to make sure it's not expired
    if (item.type === 'video' && tokens?.idToken) {
      try {
        const refreshedMedia = await uploadService.refreshMediaUrl(item.id, tokens.idToken);
        if (refreshedMedia) {
          // Update the media item in state with the refreshed URL
          setSelectedMedia(refreshedMedia);
          // Also update it in the main list
          setMediaItems(prevItems => 
            prevItems.map(prevItem => 
              prevItem.id === item.id ? refreshedMedia : prevItem
            )
          );
        }
      } catch (error) {
        console.error("Error refreshing video URL:", error);
      }
    }
  };

  // Start editing metadata
  const handleStartEditMetadata = () => {
    setEditingMetadata(true);
  };

  // Cancel editing metadata
  const handleCancelEditMetadata = () => {
    if (selectedMedia) {
      setUpdatedAltText(selectedMedia.altText || '');
      setUpdatedCategory(selectedMedia.category || '');
      setUpdatedTags(selectedMedia.tags || []);
    }
    setEditingMetadata(false);
  };

  // Save metadata changes
  const handleSaveMetadata = async () => {
    if (!selectedMedia || !tokens?.idToken) {
      return;
    }

    try {
      setUpdatingMetadata(true);
      
      const updatedMedia = await uploadService.updateMediaMetadata(
        selectedMedia.id,
        {
          altText: updatedAltText,
          category: updatedCategory,
          tags: updatedTags
        },
        tokens.idToken
      );

      // Update the media in the local state
      setMediaItems(prevItems => 
        prevItems.map(item => 
          item.id === selectedMedia.id ? updatedMedia : item
        )
      );

      setSelectedMedia(updatedMedia);
      setEditingMetadata(false);
      toast.success('Media details updated successfully');
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error('Failed to update media details');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!mediaToDelete || !tokens?.idToken) {
      return;
    }

    try {
      setDeleting(true);
      
      const success = await uploadService.deleteMedia(mediaToDelete, tokens.idToken);
      
      if (success) {
        // Remove item from local state
        const updatedItems = mediaItems.filter(item => item.id !== mediaToDelete);
        setMediaItems(updatedItems);
        setFilteredItems(updatedItems.filter(item => 
          applyFilters(item, searchTerm, mediaType)
        ));
        
        // Update storage usage
        const usage = await uploadService.getStorageUsage(tokens.idToken);
        setStorageUsage(usage);
        
        toast.success('Media deleted successfully');
        setDeleteConfirmOpen(false);
        setMediaToDelete(null);
        
        // If the deleted item was being viewed in the detail dialog, close it
        if (selectedMedia?.id === mediaToDelete) {
          setOpenDetailDialog(false);
          setSelectedMedia(null);
        }
      }
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    } finally {
      setDeleting(false);
    }
  };

  // Close file details
  const handleCloseDetails = () => {
    setOpenDetailDialog(false);
  };

  // Copy URL to clipboard
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success('URL copied to clipboard');
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        toast.error('Failed to copy URL');
      });
    
    // Close context menu if open
    if (contextMenu) {
      setContextMenu(null);
    }
  };

  // Open context menu
  const handleContextMenu = (event: React.MouseEvent, mediaId: string) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      mediaId,
    });
  };

  // Close context menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Format file size for display
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render preview for media item
  const renderMediaPreview = (item: MediaItem) => {
    if (item.type === 'image') {
      return (
        <CardMedia
          component="img"
          height={viewMode === 'grid' ? 140 : 100}
          image={item.url}
          alt={item.altText || item.name}
          sx={{ 
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': { transform: 'scale(1.05)' }
          }}
        />
      );
    } else { // video
      return (
        <Box position="relative" height={viewMode === 'grid' ? 140 : 100}>
          <CardMedia
            component="video"
            height={viewMode === 'grid' ? 140 : 100}
            src={item.url}
            muted
            preload="metadata"
            sx={{
              objectFit: 'cover',
              width: '100%',
              bgcolor: 'black',
            }}
            onMouseOver={(e) => {
              const video = e.target as HTMLVideoElement;
              if (video.paused) video.play();
            }}
            onMouseOut={(e) => {
              const video = e.target as HTMLVideoElement;
              if (!video.paused) {
                video.pause();
                video.currentTime = 0;
              }
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.3)',
              opacity: 0.7,
              transition: 'opacity 0.3s',
              '&:hover': {
                opacity: 0.3,
              }
            }}
          >
            <PlayCircleOutlineIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            Video
          </Typography>
        </Box>
      );
    }
  };

  // Render grid view
  const renderGridView = () => {
    return (
      <Grid container spacing={3}>
        {currentItems.map(item => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'box-shadow 0.3s ease, transform 0.2s ease',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
              onContextMenu={(e) => handleContextMenu(e, item.id)}
            >
              <Box sx={{ position: 'relative' }}>
                {renderMediaPreview(item)}
                <IconButton
                  sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.8)' }}
                  size="small"
                  onClick={() => handleOpenDetails(item)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Typography variant="body2" noWrap title={item.name}>
                  {item.name}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(item.size)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.uploadDate.toLocaleDateString()}
                  </Typography>
                </Box>
                {item.tags && item.tags.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {item.tags.slice(0, 2).map(tag => (
                      <Chip 
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    ))}
                    {item.tags.length > 2 && (
                      <Typography variant="caption" color="text.secondary">
                        +{item.tags.length - 2} more
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Render list view
  const renderListView = () => {
    return (
      <Box>
        {currentItems.map(item => (
          <Paper
            key={item.id}
            sx={{
              p: 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              transition: 'box-shadow 0.3s ease',
              '&:hover': {
                boxShadow: 3
              }
            }}
            onContextMenu={(e) => handleContextMenu(e, item.id)}
          >
            <Box sx={{ width: 100, height: 100, mr: 2, flexShrink: 0 }}>
              {renderMediaPreview(item)}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1">{item.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatFileSize(item.size)} • {item.uploadDate.toLocaleDateString()}
              </Typography>
              {item.category && (
                <Typography variant="body2">
                  Category: {item.category}
                </Typography>
              )}
              {item.tags && item.tags.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {item.tags.map(tag => (
                    <Chip 
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            <Box>
              <Tooltip title="View details">
                <IconButton onClick={() => handleOpenDetails(item)}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  // Render media detail dialog content
  const renderMediaDetailContent = () => {
    if (!selectedMedia) return null;

    return (
      <>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              {selectedMedia.type === 'image' ? (
                <img 
                  src={selectedMedia.url} 
                  alt={selectedMedia.altText || selectedMedia.name}
                  style={{ 
                    width: '100%', 
                    maxHeight: '300px', 
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
              ) : (
                <Box 
                  component="video"
                  src={selectedMedia.url}
                  controls
                  autoPlay={false}
                  sx={{ 
                    width: '100%',
                    height: '300px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    backgroundColor: '#000'
                  }}
                />
              )}
            </Box>
            
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={() => handleCopyUrl(selectedMedia.url)}
                variant="outlined"
                size="small"
              >
                Copy URL
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setMediaToDelete(selectedMedia.id);
                  setDeleteConfirmOpen(true);
                }}
                variant="outlined"
                color="error"
                size="small"
              >
                Delete
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {selectedMedia.name}
              </Typography>
              {!editingMetadata ? (
                <Button
                  startIcon={<EditIcon />}
                  onClick={handleStartEditMetadata}
                  size="small"
                >
                  Edit Details
                </Button>
              ) : (
                <Box>
                  <Button 
                    onClick={handleCancelEditMetadata}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveMetadata}
                    variant="contained" 
                    color="primary"
                    size="small"
                    disabled={updatingMetadata}
                  >
                    {updatingMetadata ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>
            
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Type:</strong> {selectedMedia.type === 'image' ? 'Image' : 'Video'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {formatFileSize(selectedMedia.size)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Uploaded:</strong> {selectedMedia.uploadDate.toLocaleDateString()}
              </Typography>
              {selectedMedia.category && !editingMetadata && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Category:</strong> {selectedMedia.category}
                </Typography>
              )}
            </Box>
            
            <TextField
              fullWidth
              label="URL"
              value={selectedMedia.url}
              InputProps={{
                readOnly: true,
              }}
              margin="dense"
              variant="outlined"
              size="small"
            />
            
            {editingMetadata ? (
              <>
                <TextField
                  fullWidth
                  label="Alt Text / Description"
                  value={updatedAltText}
                  onChange={(e) => setUpdatedAltText(e.target.value)}
                  margin="dense"
                  variant="outlined"
                  size="small"
                />
                
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={updatedCategory}
                    onChange={(e) => setUpdatedCategory(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Healthcare">Healthcare</MenuItem>
                    <MenuItem value="Hospice Care">Hospice Care</MenuItem>
                    <MenuItem value="Elderly Care">Elderly Care</MenuItem>
                    <MenuItem value="Community">Community</MenuItem>
                    <MenuItem value="Educational">Educational</MenuItem>
                    <MenuItem value="Events">Events</MenuItem>
                  </Select>
                </FormControl>
                
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom>
                    Tags
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Add tags (press Enter after each tag)"
                    value={tagInput}
                    onChange={handleTagInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagInput.trim()) {
                        e.preventDefault();
                        if (!updatedTags.includes(tagInput.trim())) {
                          setUpdatedTags([...updatedTags, tagInput.trim()]);
                        }
                        setTagInput('');
                      }
                    }}
                    margin="dense"
                    size="small"
                  />
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {updatedTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => setUpdatedTags(updatedTags.filter(t => t !== tag))}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Alt Text / Description"
                  value={selectedMedia.altText || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  margin="dense"
                  variant="outlined"
                  size="small"
                />
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Tags
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedMedia.tags && selectedMedia.tags.length > 0 ? (
                    selectedMedia.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tags
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Grid>
        </Grid>
      </>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Media Library
      </Typography>
      
      {/* Storage Usage Card */}
      {storageUsage && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Storage Usage
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  {storageUsage.usedStorageGB.toFixed(2)} GB of {storageUsage.totalStorageGB} GB
                </Typography>
                <Typography 
                  variant="body2" 
                  color={
                    storageUsage.usagePercentage > 90 
                      ? 'error.main' 
                      : storageUsage.usagePercentage > 70
                        ? 'warning.main'
                        : 'success.main'
                  }
                >
                  ({storageUsage.usagePercentage.toFixed(1)}%)
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={storageUsage.usagePercentage}
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: storageUsage.usagePercentage > 90 
                      ? theme.palette.error.main 
                      : storageUsage.usagePercentage > 70
                        ? theme.palette.warning.main
                        : theme.palette.success.main
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  <strong>Total Files:</strong> {storageUsage.mediaCount}
                </Typography>
                <Typography variant="body2">
                  <strong>Images:</strong> {storageUsage.imageCount}
                </Typography>
                <Typography variant="body2">
                  <strong>Videos:</strong> {storageUsage.videoCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                <strong>Available Space:</strong> {storageUsage.remainingStorageGB.toFixed(2)} GB
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {/* Filters and search */}
      <Box mb={4}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, tag, or category..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={7}>
            <Box display="flex" justifyContent="space-between">
              <Box>
                {/* Media type filter */}
                <ToggleButtonGroup
                  value={mediaType}
                  exclusive
                  onChange={handleMediaTypeChange}
                  aria-label="media type filter"
                  size="small"
                >
                  <ToggleButton value="all">
                    <Tooltip title="All Media">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InsertDriveFileIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>All</Box>
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="image">
                    <Tooltip title="Images Only">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ImageIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>Images</Box>
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="video">
                    <Tooltip title="Videos Only">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VideoLibraryIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>Videos</Box>
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              <Box display="flex" alignItems="center">
                {/* View mode toggle */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label="view mode"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <ToggleButton value="grid">
                    <Tooltip title="Grid View">
                      <GridViewIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="list">
                    <Tooltip title="List View">
                      <ViewListIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
                
                {/* Sort options */}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={sortBy}
                      onChange={handleSortChange}
                      displayEmpty
                      variant="outlined"
                      startAdornment={
                        <InputAdornment position="start">
                          <SortIcon fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="date">Date</MenuItem>
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="size">Size</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}>
                    <IconButton onClick={toggleSortDirection} size="small">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </IconButton>
                  </Tooltip>
                </Box>
                
                {/* Upload button */}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenUploadDialog}
                  sx={{ ml: 2 }}
                >
                  Upload
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      
      {/* Results count and pagination info */}
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Showing {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredItems.length)} of {filteredItems.length} items
        </Typography>
      </Box>
      
      {/* Media grid or list */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      ) : filteredItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No media items found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filters, or upload new media
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenUploadDialog}
            sx={{ mt: 2 }}
          >
            Upload New Media
          </Button>
        </Paper>
      ) : (
        viewMode === 'grid' ? renderGridView() : renderListView()
      )}
      
      {/* Pagination */}
      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      
      {/* Upload dialog */}
      <Dialog
        open={openUploadDialog}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Media
          <IconButton
            aria-label="close"
            onClick={handleCloseUploadDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <MediaUpload
            mediaType="both"
            onUploadSuccess={handleUploadSuccess}
            onCancel={handleCloseUploadDialog}
          />
          {uploadedFileUrl && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCloseUploadDialog}
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveUpload}
                disabled={uploadingFile}
                startIcon={uploadingFile ? <CircularProgress size={20} /> : null}
              >
                {uploadingFile ? 'Saving...' : 'Add to Library'}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Media detail dialog */}
      <Dialog open={openDetailDialog} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        {selectedMedia && (
          <>
            <DialogTitle>
              Media Details
              <IconButton
                aria-label="close"
                onClick={handleCloseDetails}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <MoreVertIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {renderMediaDetailContent()}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this media file? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu && (
          <>
            <MenuItem onClick={() => {
              const item = mediaItems.find(item => item.id === contextMenu.mediaId);
              if (item) {
                handleOpenDetails(item);
              }
              handleCloseContextMenu();
            }}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Details</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              const item = mediaItems.find(item => item.id === contextMenu.mediaId);
              if (item) {
                handleCopyUrl(item.url);
              }
            }}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy URL</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleConfirmDelete(contextMenu.mediaId)}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default AdminMedia; 